import { resolveLiveSource, type LiveSource } from '@auctisync/api-client';
import { useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import { useSession } from './useSession';

/**
 * Where a sale's live updates come from — the engine's feed, or the Supabase
 * book's Realtime — asked of the shared client, never decided here.
 *
 * `resolveLiveSource` reads the bid path (`get_bid_path`), so a page's prices
 * and its bids can never come from different books (memory:
 * feedback_engine_is_the_default_supabase_is_the_parachute). Any lot of the
 * sale answers for all of it; a list page passes any one of its lots.
 *
 * A FAILED LOOKUP IS NOT GUESSED. It is asked again, backing off from 2 s to
 * 30 s, and `failed` is true meanwhile so the page can say it is not live yet.
 * Asked again when the viewer signs in or out — a database without migration
 * 20261008170000 answers a signed-out visitor "Realtime" for that reason
 * alone, and signing in changes the answer.
 *
 * The answer object is kept when a re-ask returns the same route, so the feed
 * built on it is not torn down and rebuilt for nothing.
 */
export function useLiveSource(lotId: string | null): {
  source: LiveSource | null;
  failed: boolean;
} {
  const { session, loading } = useSession();
  const userId = session?.user.id ?? null;
  const [source, setSource] = useState<LiveSource | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (lotId === null) {
      setSource(null);
      setFailed(false);
      return;
    }
    // Who is asking decides nothing yet: wait for the session, or a signed-in
    // visitor would ask twice on every page load.
    if (loading) return;
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | null = null;
    let attempt = 0;

    const ask = async (fresh: boolean): Promise<void> => {
      const answer = await resolveLiveSource(supabase, lotId, { fresh });
      if (cancelled) return;
      if (answer.ok) {
        setFailed(false);
        setSource((prev) => (sameSource(prev, answer.source) ? prev : answer.source));
        return;
      }
      setFailed(true);
      const delay = Math.min(30_000, 2_000 * 2 ** attempt);
      attempt += 1;
      timer = setTimeout(() => {
        timer = null;
        void ask(true);
      }, delay);
    };

    // The route cache is safe to use first: it never holds the answer a
    // signed-out visitor was given (`getBidRoute` does not remember those),
    // and it is the one the bid path reads, so prices and bids still agree.
    void ask(false);
    return () => {
      cancelled = true;
      if (timer !== null) clearTimeout(timer);
    };
  }, [lotId, userId, loading]);

  return { source, failed };
}

/** Same route, field for field. */
export function sameSource(a: LiveSource | null, b: LiveSource | null): boolean {
  if (a === null || b === null) return a === b;
  if (a.kind === 'engine' && b.kind === 'engine') {
    return (
      a.engineUrl === b.engineUrl &&
      a.engineAuctionId === b.engineAuctionId &&
      a.houseId === b.houseId &&
      a.saleId === b.saleId
    );
  }
  if (a.kind === 'supabase' && b.kind === 'supabase') return a.reason === b.reason;
  return false;
}
